import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { SearchableSelect } from '../components/SearchableSelect';
import DateTimePicker from '../components/DateTimePicker';
import {
  Plus,
  Play,
  Pause,
  Search,
  Copy,
  Trash2,
  Calendar,
  Clock,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  BarChart2,
  Mail,
  ChevronUp,
  ChevronsUpDown
} from 'lucide-react';

const campaignSchema = z.object({
  name: z.string().min(1, 'Campaign name is required'),
  subject: z.string().optional(),
  senderName: z.string().min(1, 'Sender display name is required'),
  senderEmail: z.string().email('Please enter a valid email address'),
  templateId: z.string().min(1, 'Please select an email template'),
  recipientListId: z.string().min(1, 'Please select a recipient list'),
  scheduledAt: z.string().optional().nullable()
});

type CampaignFormValues = z.infer<typeof campaignSchema>;

export const Campaigns: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean; title: string; message: string; onConfirm: () => void;
  }>({ open: false, title: '', message: '', onConfirm: () => {} });
  const openConfirm = (title: string, message: string, onConfirm: () => void) =>
    setConfirmDialog({ open: true, title, message, onConfirm });
  const closeConfirm = () => setConfirmDialog(d => ({ ...d, open: false }));

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortKey, setSortKey] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [scheduleDateTime, setScheduleDateTime] = useState('');
  const [testEmailAddress, setTestEmailAddress] = useState('');

  // Fetch campaigns — poll every 10s while any campaign is actively sending
  const { data: campaignsData, isLoading } = useQuery({
    queryKey: ['campaigns', page, statusFilter, debouncedSearch],
    queryFn: async () => {
      const params: any = { page, limit: 10 };
      if (statusFilter !== 'ALL') params.status = statusFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await apiClient.get('/campaigns', { params });
      return res.data;
    },
    refetchInterval: (data: any) => {
      const list = data?.campaigns || [];
      return list.some((c: any) => c.status === 'PROCESSING') ? 10_000 : false;
    }
  });

  // Fetch templates for dropdown selection
  const { data: templates } = useQuery({
    queryKey: ['templates'],
    queryFn: async () => {
      const res = await apiClient.get('/templates');
      return res.data;
    }
  });

  // Fetch recipient lists for dropdown selection
  const { data: lists } = useQuery({
    queryKey: ['lists'],
    queryFn: async () => {
      const res = await apiClient.get('/lists');
      return res.data;
    }
  });

  // Fetch verified sender accounts
  const { data: verifiedSenders } = useQuery({
    queryKey: ['verifiedSenders'],
    queryFn: async () => {
      const res = await apiClient.get('/settings/senders/verified');
      return res.data;
    }
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignSchema)
  });

  useEffect(() => {
    if (isCreateModalOpen && verifiedSenders && verifiedSenders.length > 0) {
      const primarySender = verifiedSenders.find((s: any) => s.isPrimary) || verifiedSenders[0];
      if (primarySender) {
        setValue('senderEmail', primarySender.email);
        setValue('senderName', primarySender.name);
      }
    }
  }, [isCreateModalOpen, verifiedSenders, setValue]);

  const templateId = watch('templateId');
  const recipientListId = watch('recipientListId');
  const senderEmail = watch('senderEmail');

  // Create Campaign Mutation
  const createMutation = useMutation({
    mutationFn: async (data: CampaignFormValues) => {
      const payload = {
        ...data,
        scheduledAt: data.scheduledAt ? new Date(data.scheduledAt).toISOString() : null
      };
      const res = await apiClient.post('/campaigns', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns']);
      showToast('Campaign created successfully', 'success');
      setIsCreateModalOpen(false);
      reset();
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error || 'Failed to create campaign';
      showToast(msg, 'error');
    }
  });

  // Send campaign immediately Mutation
  const sendMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/campaigns/${id}/send`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns']);
      showToast('Campaign sending process initiated', 'success');
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error || 'Failed to send campaign';
      showToast(msg, 'error');
    }
  });

  // Schedule campaign Mutation
  const scheduleMutation = useMutation({
    mutationFn: async ({ id, date }: { id: string; date: string }) => {
      const res = await apiClient.post(`/campaigns/${id}/schedule`, {
        scheduledAt: new Date(date).toISOString()
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns']);
      showToast('Campaign scheduled successfully', 'success');
      setIsScheduleModalOpen(false);
      setScheduleDateTime('');
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error || 'Failed to schedule campaign';
      showToast(msg, 'error');
    }
  });

  // Pause campaign Mutation
  const pauseMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/campaigns/${id}/pause`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns']);
      showToast('Campaign sending paused', 'success');
    },
    onError: (err: any) => {
      showToast('Failed to pause campaign', 'error');
    }
  });

  // Resume campaign Mutation
  const resumeMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/campaigns/${id}/resume`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns']);
      showToast('Campaign sending resumed', 'success');
    },
    onError: (err: any) => {
      showToast('Failed to resume campaign', 'error');
    }
  });

  // Duplicate campaign Mutation
  const duplicateMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/campaigns/${id}/duplicate`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns']);
      showToast('Campaign cloned successfully', 'success');
    },
    onError: (err: any) => {
      showToast('Failed to clone campaign', 'error');
    }
  });

  // Delete campaign Mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/campaigns/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns']);
      showToast('Campaign deleted successfully', 'success');
    },
    onError: (err: any) => {
      showToast('Failed to delete campaign', 'error');
    }
  });

  // Send test email Mutation
  const sendTestEmailMutation = useMutation({
    mutationFn: async ({ id, toEmail }: { id: string; toEmail: string }) => {
      const res = await apiClient.post(`/campaigns/${id}/test`, { toEmail });
      return res.data;
    },
    onSuccess: () => {
      showToast('Test email sent successfully', 'success');
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error || 'Failed to send test email';
      showToast(msg, 'error');
    }
  });

  const onSubmit = (data: CampaignFormValues) => {
    createMutation.mutate(data);
  };

  const handleOpenScheduleModal = (id: string) => {
    setSelectedCampaignId(id);
    setIsScheduleModalOpen(true);
    if (user?.email) {
      setTestEmailAddress(user.email);
    }
  };

  const handleSendTestEmail = () => {
    if (selectedCampaignId && testEmailAddress) {
      sendTestEmailMutation.mutate({ id: selectedCampaignId, toEmail: testEmailAddress });
    }
  };

  const handleScheduleSubmit = () => {
    if (selectedCampaignId && scheduleDateTime) {
      scheduleMutation.mutate({ id: selectedCampaignId, date: scheduleDateTime });
    }
  };

  const rawCampaigns = campaignsData?.campaigns || [];
  const pagination = campaignsData?.pagination || { total: 0, pages: 1 };

  const campaigns = [...rawCampaigns].sort((a: any, b: any) => {
    let av: any, bv: any;
    if (sortKey === 'name') { av = a.name?.toLowerCase(); bv = b.name?.toLowerCase(); }
    else if (sortKey === 'status') { av = a.status; bv = b.status; }
    else if (sortKey === 'sent') { av = a.stats?.sent || 0; bv = b.stats?.sent || 0; }
    else if (sortKey === 'openRate') {
      av = a.stats?.delivered > 0 ? a.stats.opened / a.stats.delivered : 0;
      bv = b.stats?.delivered > 0 ? b.stats.opened / b.stats.delivered : 0;
    }
    else { av = new Date(a.createdAt).getTime(); bv = new Date(b.createdAt).getTime(); }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const STATUS_PILLS = [
    { key: 'ALL', label: 'All' },
    { key: 'DRAFT', label: 'Draft' },
    { key: 'SCHEDULED', label: 'Scheduled' },
    { key: 'PROCESSING', label: 'Sending' },
    { key: 'SENT', label: 'Sent' },
    { key: 'PAUSED', label: 'Paused' },
    { key: 'FAILED', label: 'Failed' },
  ];

  const BADGE: Record<string, string> = {
    DRAFT:               'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700',
    SCHEDULED:           'bg-primary-500/10 text-primary-600 dark:text-primary-500 border-primary-500/20',
    PROCESSING:          'bg-primary-500/10 text-primary-600 dark:text-primary-500 border-primary-500/20 animate-pulse',
    SENT:                'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20',
    FAILED:              'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20',
    PAUSED:              'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    PAUSED_BOUNCE_LIMIT: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20',
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight uppercase">Campaigns</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {pagination.total ?? campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''} · Create, monitor, and scale email campaigns.
          </p>
        </div>
        <button onClick={() => setIsCreateModalOpen(true)} className="btn-primary flex items-center justify-center gap-2 self-start">
          <Plus size={16} /> Create Campaign
        </button>
      </div>

      {/* Search + Status filters row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search campaigns..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="form-input pl-8 pr-3 py-1.5 text-sm w-56"
          />
        </div>
        <div className="h-5 w-px bg-slate-200 dark:bg-white/10" />
      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2">
        {STATUS_PILLS.map(p => (
          <button
            key={p.key}
            onClick={() => { setStatusFilter(p.key); setPage(1); }}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
              statusFilter === p.key
                ? 'bg-primary-500 text-white border-primary-500 shadow-sm shadow-primary-500/20'
                : 'bg-white dark:bg-white/5 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-white/10 hover:border-primary-500/40 hover:text-primary-500'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      </div>

      {/* Campaigns table */}
      <div className="glass-panel rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 dark:border-white/5 text-slate-400 text-[11px] font-semibold uppercase tracking-wider">
                {([
                  { key: 'name', label: 'Campaign' },
                  { key: null, label: 'Sender' },
                  { key: null, label: 'List / Template' },
                  { key: 'status', label: 'Status' },
                  { key: 'sent', label: 'Performance' },
                  { key: 'createdAt', label: 'Created' },
                  { key: null, label: 'Actions', right: true },
                ] as { key: string | null; label: string; right?: boolean }[]).map(col => (
                  <th
                    key={col.label}
                    className={`py-3 px-5 ${col.right ? 'text-right' : ''} ${col.key ? 'cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200 transition-colors' : ''}`}
                    onClick={() => col.key && toggleSort(col.key)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {col.key && (
                        sortKey === col.key
                          ? <ChevronUp size={11} className={`transition-transform ${sortDir === 'desc' ? 'rotate-180' : ''}`} />
                          : <ChevronsUpDown size={11} className="opacity-40" />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {isLoading ? (
                <tr><td colSpan={7} className="text-center py-12">
                  <div className="w-7 h-7 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </td></tr>
              ) : campaigns.map((camp: any) => {
                const s = camp.stats || {};
                const sent = s.sent || 0;
                const delivered = s.delivered || 0;
                const opened = s.opened || 0;
                const clicked = s.clicked || 0;
                const bounced = s.bounced || 0;
                const dlvrPct  = sent > 0 ? (delivered / sent) * 100 : 0;
                const openPct  = delivered > 0 ? (opened / delivered) * 100 : 0;
                const clickPct = delivered > 0 ? (clicked / delivered) * 100 : 0;
                const bouncePct = sent > 0 ? (bounced / sent) * 100 : 0;
                const threshold = (camp.bounceRateThreshold || 0.09) * 100;
                const bounceIsHigh = bouncePct >= threshold;
                const bounceIsWarn = !bounceIsHigh && bouncePct >= threshold * 0.7;
                const isLive = camp.status === 'PROCESSING' || camp.status === 'PAUSED_BOUNCE_LIMIT';

                return (
                  <tr key={camp.id} className="hover:bg-slate-50/70 dark:hover:bg-white/[0.03] transition-colors group">
                    {/* Campaign name + subject */}
                    <td className="py-4 px-5 max-w-[220px]">
                      <button
                        onClick={() => navigate(`/analytics?campaign=${camp.id}`)}
                        className="text-left w-full"
                      >
                        <div className="font-semibold text-slate-800 dark:text-white text-sm truncate group-hover:text-primary-500 transition-colors">
                          {camp.name}
                        </div>
                        <div className="text-[11px] text-slate-400 truncate mt-0.5">{camp.subject}</div>
                      </button>
                    </td>

                    {/* Sender */}
                    <td className="py-4 px-5">
                      <div className="text-xs font-medium text-slate-700 dark:text-slate-200">{camp.senderName}</div>
                      <div className="text-[11px] text-slate-400 truncate max-w-[160px]">{camp.senderEmail}</div>
                    </td>

                    {/* List / Template */}
                    <td className="py-4 px-5">
                      <div className="text-[11px] text-slate-500 dark:text-slate-400">
                        <span className="font-medium text-slate-700 dark:text-slate-200">{camp.recipientListId?.name || 'Deleted list'}</span>
                      </div>
                      <div className="text-[11px] text-slate-400 truncate max-w-[160px]">{camp.templateId?.name || 'Deleted template'}</div>
                    </td>

                    {/* Status */}
                    <td className="py-4 px-5">
                      <div className="flex flex-col gap-1.5 items-start">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${BADGE[camp.status] || BADGE.DRAFT}`}>
                          {camp.status === 'PAUSED_BOUNCE_LIMIT' ? 'BOUNCE LIMIT' : camp.status}
                        </span>
                        {camp.status === 'SCHEDULED' && camp.scheduledAt && (
                          <span className="text-[10px] text-slate-400 flex items-center gap-1">
                            <Clock size={9} /> {new Date(camp.scheduledAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        {camp.status === 'PAUSED_BOUNCE_LIMIT' && (
                          <span className="text-[10px] text-rose-500 flex items-center gap-1">
                            <AlertTriangle size={9} /> Bounce limit exceeded
                          </span>
                        )}
                        {(camp.status === 'PROCESSING' || camp.status === 'PAUSED_BOUNCE_LIMIT') && camp.chunksTotal > 0 && (
                          <div className="w-32 space-y-0.5">
                            <div className="flex gap-px">
                              {Array.from({ length: camp.chunksTotal }, (_: any, i: number) => (
                                <div key={i} className={`h-1 flex-1 rounded-sm transition-colors ${
                                  i < camp.chunksSent ? 'bg-emerald-500'
                                  : camp.status === 'PAUSED_BOUNCE_LIMIT' && i === camp.chunksSent ? 'bg-rose-500'
                                  : 'bg-slate-200 dark:bg-slate-700'
                                }`} />
                              ))}
                            </div>
                            <span className="text-[9px] text-slate-400">{camp.chunksSent}/{camp.chunksTotal} chunks</span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Performance — badge style */}
                    <td className="py-4 px-5">
                      {sent === 0 ? (
                        <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
                      ) : (
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-300 tabular-nums">
                              {sent.toLocaleString()} sent
                            </span>
                            <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 tabular-nums">
                              {dlvrPct.toFixed(1)}% dlvr
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-primary-500/10 text-primary-600 dark:text-primary-500 tabular-nums">
                              {openPct.toFixed(1)}% open
                            </span>
                            <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 tabular-nums">
                              {clickPct.toFixed(1)}% click
                            </span>
                          </div>
                          {(isLive || bounced > 0) && (
                            <span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold tabular-nums w-fit ${bounceIsHigh ? 'bg-rose-500/15 text-rose-600 dark:text-rose-400' : bounceIsWarn ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'bg-rose-500/10 text-rose-500 dark:text-rose-400'}`}>
                              {bouncePct.toFixed(1)}% bounce{bounceIsHigh ? ' ⚠' : ''}
                            </span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Created date */}
                    <td className="py-4 px-5 whitespace-nowrap">
                      <div className="text-xs text-slate-600 dark:text-slate-300">
                        {new Date(camp.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {new Date(camp.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="py-4 px-5">
                      <div className="flex items-center justify-end gap-1">
                        {/* Analytics link */}
                        <button onClick={() => navigate(`/analytics?campaign=${camp.id}`)} className="text-slate-400 hover:text-primary-500 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors" title="View Analytics">
                          <BarChart2 size={15} />
                        </button>

                        {/* Send / Pause / Resume */}
                        {(camp.status === 'DRAFT' || camp.status === 'FAILED') && (
                          <button onClick={() => sendMutation.mutate(camp.id)} className="text-slate-400 hover:text-emerald-500 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors" title="Send Now">
                            <Play size={15} />
                          </button>
                        )}
                        {(camp.status === 'DRAFT' || camp.status === 'FAILED' || camp.status === 'PAUSED') && (
                          <button onClick={() => handleOpenScheduleModal(camp.id)} className="text-slate-400 hover:text-primary-500 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors" title="Schedule">
                            <Calendar size={15} />
                          </button>
                        )}
                        {camp.status === 'PAUSED' && (
                          <button onClick={() => resumeMutation.mutate(camp.id)} className="text-slate-400 hover:text-emerald-500 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors" title="Resume">
                            <Play size={15} />
                          </button>
                        )}
                        {(camp.status === 'PROCESSING' || camp.status === 'SCHEDULED') && (
                          <button onClick={() => pauseMutation.mutate(camp.id)} className="text-slate-400 hover:text-amber-500 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors" title="Pause">
                            <Pause size={15} />
                          </button>
                        )}
                        {camp.status === 'SCHEDULED' && (
                          <button onClick={() => handleOpenScheduleModal(camp.id)} className="text-slate-400 hover:text-primary-500 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors" title="Reschedule">
                            <Calendar size={15} />
                          </button>
                        )}
                        {camp.status === 'PAUSED_BOUNCE_LIMIT' && (
                          <button
                            onClick={() => openConfirm('Override Bounce Limit', `Bounce rate exceeded ${((camp.bounceRateThreshold || 0.09) * 100).toFixed(0)}% threshold. Resume and continue sending?`, () => resumeMutation.mutate(camp.id))}
                            className="text-slate-400 hover:text-emerald-500 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors" title="Resume (override)"
                          >
                            <Play size={15} />
                          </button>
                        )}
                        <button onClick={() => duplicateMutation.mutate(camp.id)} className="text-slate-400 hover:text-primary-500 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors" title="Duplicate">
                          <Copy size={15} />
                        </button>
                        {camp.status !== 'PROCESSING' && camp.status !== 'PAUSED_BOUNCE_LIMIT' && (
                          <button
                            onClick={() => openConfirm('Delete Campaign', `Delete "${camp.name}"? This cannot be undone.`, () => deleteMutation.mutate(camp.id))}
                            className="text-slate-400 hover:text-rose-500 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors" title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!isLoading && campaigns.length === 0 && (
                <tr><td colSpan={7} className="text-center py-16">
                  <div className="flex flex-col items-center gap-3 text-slate-400">
                    <Mail size={32} className="opacity-30" />
                    <div className="text-sm">{statusFilter !== 'ALL' ? `No ${statusFilter.toLowerCase()} campaigns` : 'No campaigns yet'}</div>
                    {statusFilter === 'ALL' && <button onClick={() => setIsCreateModalOpen(true)} className="btn-primary text-xs px-4 py-2 mt-1">Create your first campaign</button>}
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-5 py-3 border-t border-slate-100 dark:border-white/5 flex items-center justify-between">
            <span className="text-xs text-slate-400">Page {page} of {pagination.pages} · {pagination.total} campaigns</span>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="btn-secondary px-3 py-1.5 flex items-center gap-1 disabled:opacity-40 text-xs">
                <ChevronLeft size={13} /> Prev
              </button>
              <button disabled={page === pagination.pages} onClick={() => setPage(p => p + 1)} className="btn-secondary px-3 py-1.5 flex items-center gap-1 disabled:opacity-40 text-xs">
                Next <ChevronRight size={13} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Campaign Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          reset();
        }}
        title="Create Campaign"
        size="lg"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Campaign Name</label>
            <input
              type="text"
              placeholder="Summer Promotion Offer"
              {...register('name')}
              className="form-input"
            />
            {errors.name && <span className="text-xs text-accent-rose">{errors.name.message}</span>}
          </div>

          {/* Hidden inputs for React Hook Form validation and submit payload */}
          <input type="hidden" {...register('senderName')} />
          <input type="hidden" {...register('senderEmail')} />

          {/* Searchable Select for Sender Identity */}
          <div className="flex flex-col gap-1.5">
            <SearchableSelect
              label="Select Sender Account"
              placeholder="-- Choose Sender --"
              options={verifiedSenders?.map((s: any) => ({
                value: s.email,
                label: s.name ? `${s.name} <${s.email}>` : s.email
              })) || []}
              value={senderEmail || ''}
              onChange={(val) => {
                const selected = verifiedSenders?.find((s: any) => s.email === val);
                if (selected) {
                  setValue('senderEmail', selected.email, { shouldValidate: true });
                  setValue('senderName', selected.name || '', { shouldValidate: true });
                }
              }}
              error={errors.senderEmail?.message || errors.senderName?.message}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Email Template Selector */}
            <div className="flex flex-col gap-1.5">
              <input type="hidden" {...register('templateId')} />
              <SearchableSelect
                label="Select Email Template"
                placeholder="-- Choose Template --"
                options={templates?.map((t: any) => ({ value: t.id, label: t.name })) || []}
                value={templateId || ''}
                onChange={(val) => setValue('templateId', val, { shouldValidate: true })}
                error={errors.templateId?.message}
              />
            </div>

            {/* Recipient List Selector */}
            <div className="flex flex-col gap-1.5">
              <input type="hidden" {...register('recipientListId')} />
              <SearchableSelect
                label="Target Recipient List"
                placeholder="-- Choose List --"
                options={lists?.map((l: any) => ({ value: l.id, label: l.name })) || []}
                value={recipientListId || ''}
                onChange={(val) => setValue('recipientListId', val, { shouldValidate: true })}
                error={errors.recipientListId?.message}
              />
            </div>
          </div>

          {/* Schedule Check (Optional) */}
          <DateTimePicker
            label="Schedule Send Date (Optional)"
            placeholder="Send immediately (or pick a date)"
            value={watch('scheduledAt') || ''}
            onChange={iso => setValue('scheduledAt', iso || null)}
            minDate={new Date()}
          />

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading}
              className="btn-primary"
            >
              {createMutation.isLoading ? 'Saving...' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Schedule Campaign Modal */}
      <Modal
        isOpen={isScheduleModalOpen}
        onClose={() => {
          setIsScheduleModalOpen(false);
          setSelectedCampaignId(null);
          setScheduleDateTime('');
          setTestEmailAddress('');
        }}
        title="Schedule Campaign Send"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-400">Choose a future date and time to automatically trigger sending.</p>
          <DateTimePicker
            label="Send Time"
            placeholder="Pick a date & time"
            value={scheduleDateTime}
            onChange={iso => setScheduleDateTime(iso)}
            minDate={new Date()}
          />

          {/* Send Test Email Section */}
          <div className="p-4 rounded-xl border border-slate-200 dark:border-white/5 bg-slate-50/50 dark:bg-white/2 space-y-3 mt-4">
            <div>
              <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Test Campaign Template</h4>
              <p className="text-[11px] text-slate-400">Send a test email to verify layout and templates before scheduling.</p>
            </div>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="test@example.com"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                className="form-input flex-1 py-2 text-sm"
              />
              <button
                type="button"
                disabled={!testEmailAddress || sendTestEmailMutation.isLoading}
                onClick={handleSendTestEmail}
                className="btn-secondary py-2 px-3 text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                {sendTestEmailMutation.isLoading ? 'Sending...' : 'Send Test'}
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
            <button
              type="button"
              onClick={() => {
                setIsScheduleModalOpen(false);
                setSelectedCampaignId(null);
                setScheduleDateTime('');
                setTestEmailAddress('');
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!scheduleDateTime || scheduleMutation.isLoading}
              onClick={handleScheduleSubmit}
              className="btn-primary"
            >
              {scheduleMutation.isLoading ? 'Scheduling...' : 'Confirm Schedule'}
            </button>
          </div>
        </div>
      </Modal>
      <ConfirmDialog
        isOpen={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Delete"
        onConfirm={() => { confirmDialog.onConfirm(); closeConfirm(); }}
        onCancel={closeConfirm}
      />
    </div>
  );
};
export default Campaigns;
